import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loading } from '@carbon/react';
import { Box, DocumentImport, Recycle, Warning } from '@carbon/react/icons';
import BranchStockComparison from 'src/components/Dashboard/BranchStockComparison';
import LowStockPanel from 'src/components/Dashboard/LowStockPanel';
import MovementsPerDay from 'src/components/Dashboard/MovementsPerDay';
import StatCard from 'src/components/Dashboard/StatCard';
import TopConsumedItems from 'src/components/Dashboard/TopConsumedItems';
import UsageWasteCategories from 'src/components/Dashboard/UsageWasteCategories';
import { useDashboardInventoryReports } from 'src/hooks/useDashboardReports';
import { useMoney } from 'src/hooks/useMoney';
import { InventoryMovementTypeEnum } from 'src/utils/inventory';

interface InventoryProps {}

const Inventory = React.memo((_: InventoryProps) => {
  const { t } = useTranslation('dashboard');
  const { decimals, formatCompact } = useMoney();
  const {
    query: { isLoading, data: report, isRefetching },
    bucket,
  } = useDashboardInventoryReports();

  return (
    <div className={'inventory'}>
      {(isLoading || isRefetching) && <Loading small />}
      <div className="stats">
        <StatCard
          title={`${t('stockValue')} (${t('asOfToday')})`}
          value={report?.inventoryReports?.totalStockValue ?? 0}
          format={formatCompact}
          decimals={decimals}
          icon={<Box size={36} />}
        />
        <StatCard title={t('lowStockItems')} value={report?.lowStockItems?.length ?? 0} icon={<Warning size={36} />} />
        <StatCard
          title={t('usageCost')}
          value={report?.inventoryReports?.usageCost ?? 0}
          format={formatCompact}
          decimals={decimals}
          icon={<DocumentImport size={36} />}
        />
        <StatCard
          title={t('wasteCost')}
          value={report?.inventoryReports?.wasteCost ?? 0}
          format={formatCompact}
          decimals={decimals}
          icon={<Recycle size={36} />}
        />
      </div>
      <div className="stats">
        <LowStockPanel
          title={`${t('lowStockItems')} (${t('asOfToday')})`}
          lowStockItems={report?.lowStockItems || []}
        />
      </div>
      <div className="stats">
        <MovementsPerDay
          movementsPerDay={report?.movementsPerDay || []}
          dateFormat={bucket === 'month' ? 'MM/YYYY' : 'DD/MM'}
          title={bucket === 'month' ? t('movementsPerMonth') : t('movementsPerDay')}
        />
      </div>
      <div className="stats">
        <UsageWasteCategories
          title={t('usageCategories')}
          usageWasteByCategory={(report?.usageWasteByCategory || []).filter(
            (item) => item.movementType === InventoryMovementTypeEnum.USAGE
          )}
        />
      </div>
      <div className="stats">
        <UsageWasteCategories
          title={t('wasteCategories')}
          usageWasteByCategory={(report?.usageWasteByCategory || []).filter(
            (item) => item.movementType === InventoryMovementTypeEnum.WASTE
          )}
        />
      </div>
      {/* Named so the marketing capture can scroll to it: this chart only renders in the
          cross-branch context, so it is the `branches` screenshot. See tests/marketing. */}
      <div className="stats stats--branch-stock">
        <BranchStockComparison stockValueByBranch={report?.inventoryReports?.stockValueByBranch || []} />
      </div>
      <div className="stats">
        <TopConsumedItems topConsumedItems={report?.topConsumedItems || []} />
      </div>
    </div>
  );
});

export default Inventory;
